import { BehaviorSubject, Observable } from 'rxjs';

/**
 * enums
 */
import { ContentPageModules } from './enums';

/**
 * interfaces
 */
import { IUserPayload } from '@identity/user';
import { IAuthorizationControl, IModulePermission } from './interfaces';

/**
 * Utility class for authorization and permission management
 * @class AuthorizationUtils
 * @description Provides comprehensive authorization control with hierarchical permission checking.
 * Implements reactive permission management using RxJS observables for real-time updates.
 * 
 * @implements {IAuthorizationControl} Authorization control interface
 * 
 * @example
 * ```typescript
 * // Initialize with user payload observable
 * const authUtils = new AuthorizationUtils(userPayload$);
 * 
 * // Check permissions
 * if (authUtils.hasViewerAccessToModule(ContentPageModules.DASHBOARD)) {
 *   // User can view dashboard
 * }
 * 
 * if (authUtils.hasAdminAccessToModule(ContentPageModules.USERS)) {
 *   // User has admin access to users module
 * }
 * ```
 * 
 * @since 1.0.0
 * @author SADIN Development Team
 */
export class AuthorizationUtils implements IAuthorizationControl {

  /**
   * BehaviorSubject for module permissions state management
   * @private
   * @readonly
   * @type {BehaviorSubject<IUserPayload['module_permissions'] | null>}
   * @description Central state management for user module permissions.
   * Provides reactive updates when user permissions change.
   */
  private readonly _modulePermissions$!: BehaviorSubject<IUserPayload['module_permissions'] | null>;

  /**
   * Current module permissions getter
   * @private
   * @readonly
   * @type {IUserPayload['module_permissions'] | null}
   * @description Provides synchronous access to current module permissions.
   * Returns null if no permissions are available.
   */
  private get _modulePermissions(): IUserPayload['module_permissions'] | null {
    return this._modulePermissions$.value
  }

  /**
   * Constructor for AuthorizationUtils
   * @constructor
   * @param {Observable<IUserPayload['module_permissions'] | null>} _payload$ - Observable stream of user module permissions
   * @description Initializes the authorization utility with reactive permission management.
   * Sets up BehaviorSubject to track permission changes in real-time.
   * 
   * @throws {Error} Throws error if payload observable is null
   * 
   * @example
   * ```typescript
   * // Initialize with user service payload observable
   * const authUtils = new AuthorizationUtils(userService.payload$);
   * 
   * // Or with custom observable
   * const authUtils = new AuthorizationUtils(userPermissions$);
   * ```
   */
  constructor(private readonly _payload$: Observable<IUserPayload['module_permissions'] | null>) {

    // check if payload$ is null
    if (_payload$ == null) throw new Error('_modulePermissionsPayload$ is null');

    // create user payload$
    this._modulePermissions$ = new BehaviorSubject<IUserPayload['module_permissions'] | null>(null)
    // .pipe(switchMap(_ => _payload$)) as BehaviorSubject<IUserPayload['module_permissions'] | null>;
    // console.log(this._modulePermissions$.value)
    _payload$.subscribe(m => this._modulePermissions$.next(m))
  }

  /**
   * Gets module permission object for a specific module
   * @public
   * @method getModulePermission
   * @param {ContentPageModules} moduleName - The module to check permissions for
   * @returns {IModulePermission | null} Permission object or null if no access
   * @description Retrieves the complete permission object for the specified module.
   * Returns null if the user has no access to the module.
   * 
   * @example
   * ```typescript
   * const permission = authUtils.getModulePermission(ContentPageModules.USERS);
   * if (permission?.read) {
   *   // User can read user data
   * }
   * 
   * if (permission?.create && permission?.update) {
   *   // User can manage user data
   * }
   * ```
   */
  public getModulePermission(moduleName: ContentPageModules): IModulePermission | null {
    return this._modulePermissions?.[moduleName] ?? null;
  }

  /**
   * Checks if user has any access to a module
   * @public
   * @method hasModulePermission
   * @param {ContentPageModules} moduleName - The module to check access for
   * @returns {boolean} True if user has any access to the module
   * @description Determines if the user has any level of access to the specified module.
   * This is the most basic permission check.
   * 
   * @example
   * ```typescript
   * if (authUtils.hasModulePermission(ContentPageModules.REPORTS)) {
   *   // User has some level of access to reports
   *   this.showReportsModule();
   * }
   * ```
   */
  public hasModulePermission(moduleName: ContentPageModules): boolean {
    
    const _hasAccess = !!this.getModulePermission(moduleName);

    return _hasAccess;

  }

  /**
   * Checks if user has viewer access to a module
   * @public
   * @method hasViewerAccessToModule
   * @param {ContentPageModules} moduleName - The module to check viewer access for
   * @returns {boolean} True if user has viewer-level access
   * @description Determines if the user can view/read data from the specified module.
   * Requires both module permission and read permission.
   * 
   * @example
   * ```typescript
   * if (authUtils.hasViewerAccessToModule(ContentPageModules.DASHBOARD)) {
   *   // User can view dashboard data
   *   this.loadDashboardData();
   * }
   * ```
   */
  public hasViewerAccessToModule(moduleName: ContentPageModules): boolean {
    return !!(this.hasModulePermission(moduleName) && this.getModulePermission(moduleName)?.read)
  }

  /**
   * Checks if user has operator access to a module
   * @public
   * @method hasOperatorAccessToModule
   * @param {ContentPageModules} moduleName - The module to check operator access for
   * @returns {boolean} True if user has operator-level access
   * @description Determines if the user can perform operational tasks in the specified module.
   * Requires viewer access plus control permission.
   * 
   * @example
   * ```typescript
   * if (authUtils.hasOperatorAccessToModule(ContentPageModules.SYSTEM)) {
   *   // User can operate system functions
   *   this.enableSystemControls();
   * }
   * ```
   */
  public hasOperatorAccessToModule(moduleName: ContentPageModules): boolean {
    // check by viewer & control permission
    return !!(this.hasViewerAccessToModule(moduleName) && this.getModulePermission(moduleName)?.control)
  }

  /**
   * Checks if user has supervisor access to a module
   * @public
   * @method hasSupervisorAccessToModule
   * @param {ContentPageModules} moduleName - The module to check supervisor access for
   * @returns {boolean} True if user has supervisor-level access
   * @description Determines if the user can manage data in the specified module.
   * Requires operator access plus create and update permissions.
   * 
   * @example
   * ```typescript
   * if (authUtils.hasSupervisorAccessToModule(ContentPageModules.USERS)) {
   *   // User can manage user data
   *   this.enableUserManagement();
   * }
   * ```
   */
  public hasSupervisorAccessToModule(moduleName: ContentPageModules): boolean {

    // get module
    const _module = this.getModulePermission(moduleName);

    // check the access
    if (_module == null) return false;

    // ES6 Destruction
    const { create, update } = _module;
    
    // check if has ManageData  permission
    const _hasManageData = (create && update)

    // check by Operator & Manage Data permission
    return !!(this.hasOperatorAccessToModule(moduleName) && _hasManageData);

  }

  /**
   * Checks if user has admin access to a module
   * @public
   * @method hasAdminAccessToModule
   * @param {ContentPageModules} moduleName - The module to check admin access for
   * @returns {boolean} True if user has admin-level access
   * @description Determines if the user has full administrative access to the specified module.
   * Requires supervisor access plus delete permission.
   * 
   * @example
   * ```typescript
   * if (authUtils.hasAdminAccessToModule(ContentPageModules.SYSTEM)) {
   *   // User has full admin access to system
   *   this.enableAdminControls();
   * }
   * ```
   */
  public hasAdminAccessToModule(moduleName: ContentPageModules): boolean {

    // get module
    const _module = this.getModulePermission(moduleName);

    // check the access
    if (_module == null) return false;

    // ES6 Destruction
    // rename key because (delete is a keyword)
    const { delete: hasDelete } = _module;

    // check by isSupervisor & delete permission
    return !!(this.hasSupervisorAccessToModule(moduleName) && hasDelete);

  }
  
}
